import { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Topbar } from '@/components/Topbar';
import { Card, SectionHeader } from '@/components/Card';
import { StatTile } from '@/components/StatTile';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import {
  Sparkles, TrendingUp, AlertTriangle, UserMinus, Zap, DollarSign, Target,
} from 'lucide-react';
import {
  aiAnalyticsService,
  AiAnalyticsResult,
  AiAnalyticsFilter,
} from '@/services/ai-analytics.service';

// ─── Date presets ─────────────────────────────────────────────────────────────

interface DateRange { from?: string; to?: string; label: string }

function todayIso() { return new Date().toISOString().split('T')[0]; }
function daysAgoIso(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

const PRESETS: DateRange[] = [
  { label: 'Today', from: todayIso(), to: todayIso() },
  { label: '7d',    from: daysAgoIso(7),  to: todayIso() },
  { label: '30d',   from: daysAgoIso(30), to: todayIso() },
  { label: '90d',   from: daysAgoIso(90), to: todayIso() },
  { label: 'All time' },
];

const fmtNum = (n: number) => n.toLocaleString();
const fmtPct = (n: number) => `${n.toFixed(1)}%`;
const fmtUsd = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;

// Friendly labels for the tool calls listed in topIntents.
const TOOL_LABELS: Record<string, string> = {
  mark_lead_qualified: 'Mark lead qualified',
  request_human_handoff: 'Request human handoff',
  remember_about_customer: 'Remember about customer',
};
function friendlyTool(name: string) {
  if (TOOL_LABELS[name]) return TOOL_LABELS[name];
  if (name.startsWith('create_')) return `Create ${name.slice('create_'.length)} record`;
  return name;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AiAnalytics() {
  const [activePreset, setActivePreset] = useState<string>('30d');
  const [filter, setFilter] = useState<AiAnalyticsFilter>({
    from: daysAgoIso(30),
    to: todayIso(),
  });
  const [data, setData] = useState<AiAnalyticsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    aiAnalyticsService
      .getOverview(filter)
      .then((r) => { if (alive) setData(r); })
      .catch((e: unknown) => {
        if (alive) {
          const msg = e instanceof Error ? e.message : 'Failed to load analytics';
          setError(msg);
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [filter]);

  const applyPreset = (p: DateRange) => {
    setActivePreset(p.label);
    setFilter({ from: p.from, to: p.to });
  };

  return (
    <div>
      <Topbar
        title="AI conversation analytics"
        description="How the SlightorCRM AI bot is performing — qualifications, handoffs, costs, intents."
        right={
          <div className="flex items-center gap-1.5 flex-wrap">
            {PRESETS.map((p) => (
              <Button
                key={p.label}
                variant={activePreset === p.label ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </Button>
            ))}
          </div>
        }
      />

      <div className="px-7 py-5 space-y-5">
        {error && (
          <Card padded>
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={16} className="mt-0.5 text-danger" />
              <div>
                <div className="text-sm font-semibold text-text-primary">Couldn&apos;t load analytics</div>
                <p className="text-xs text-text-muted mt-0.5">{error}</p>
              </div>
            </div>
          </Card>
        )}

        {loading && !data && (
          <Card padded>
            <div className="py-12 text-center text-sm text-text-muted">Loading…</div>
          </Card>
        )}

        {data && data.summary.totalAiReplies === 0 && !loading && (
          <EmptyState
            icon={<Sparkles size={28} />}
            title="No AI replies yet in this window"
            description="Once the AI fallback responds to inbound WhatsApp messages, those conversations show up here. Make sure WhatsApp AI fallback is enabled in Settings → AI."
          />
        )}

        {data && data.summary.totalAiReplies > 0 && (
          <>
            {/* ── KPI tiles ──────────────────────────────────────────────────── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              <StatTile
                label="Conversations"
                value={fmtNum(data.summary.conversationsWithAi)}
                icon={<Sparkles size={16} />}
                accent="gold"
              />
              <StatTile
                label="AI replies"
                value={fmtNum(data.summary.totalAiReplies)}
                icon={<Zap size={16} />}
                accent="blue"
              />
              <StatTile
                label="Qualification rate"
                value={fmtPct(data.summary.qualificationRate)}
                delta={`${fmtNum(data.summary.aiQualifiedLeads)} qualified`}
                trend="up"
                icon={<Target size={16} />}
                accent="green"
              />
              <StatTile
                label="Cost"
                value={fmtUsd(data.summary.totalCostUsd)}
                delta={`${fmtNum(data.summary.totalTokens)} tokens`}
                icon={<DollarSign size={16} />}
                accent="amber"
              />
              <StatTile
                label="Human handoffs"
                value={fmtNum(data.summary.humanHandoffsRequested)}
                icon={<UserMinus size={16} />}
                accent="amber"
              />
              <StatTile
                label="Max-turn drop-offs"
                value={fmtNum(data.summary.maxTurnsDropoffs)}
                icon={<AlertTriangle size={16} />}
                accent="red"
              />
              <StatTile
                label="Cost cap hits"
                value={fmtNum(data.summary.costCapHits)}
                icon={<AlertTriangle size={16} />}
                accent={data.summary.costCapHits > 0 ? 'red' : 'gold'}
              />
              <StatTile
                label="Avg tokens / reply"
                value={fmtNum(data.summary.avgTokensPerReply)}
                icon={<TrendingUp size={16} />}
                accent="blue"
              />
            </div>

            {/* ── Time series ────────────────────────────────────────────────── */}
            <Card padded>
              <SectionHeader
                title="Daily activity"
                subtitle="AI replies, qualified leads, and cost over time"
                icon={<TrendingUp size={16} />}
              />
              {data.series.length === 0 ? (
                <div className="py-6 text-center text-xs text-text-muted">
                  Not enough data to plot a trend yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={data.series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: 'var(--surface)', borderColor: 'var(--border)', fontSize: 12 }}
                      labelStyle={{ color: 'var(--text-secondary)' }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line yAxisId="left" type="monotone" dataKey="aiReplies" name="AI replies" stroke="#3b82f6" strokeWidth={2} />
                    <Line yAxisId="left" type="monotone" dataKey="qualifiedLeads" name="Qualified" stroke="#22c55e" strokeWidth={2} />
                    <Line yAxisId="right" type="monotone" dataKey="costUsd" name="Cost (USD)" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>

            {/* ── Top intents (tool calls) ──────────────────────────────────── */}
            <Card padded>
              <SectionHeader
                title="Top intents"
                subtitle="Which AI tools are firing — proxy for what the bot is actually doing for customers"
                icon={<Target size={16} />}
              />
              {data.topIntents.length === 0 ? (
                <div className="py-6 text-center text-xs text-text-muted">
                  No tool calls in this window.
                </div>
              ) : (
                <div className="space-y-2">
                  {data.topIntents.map((row) => {
                    const successPct = row.count > 0 ? Math.round((row.successCount / row.count) * 100) : 0;
                    const max = Math.max(...data.topIntents.map((r) => r.count), 1);
                    const widthPct = Math.round((row.count / max) * 100);
                    return (
                      <div key={row.toolName} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-text-primary">{friendlyTool(row.toolName)}</span>
                          <div className="flex items-center gap-3 text-text-muted tabular-nums">
                            <span className="text-success">
                              {row.successCount}/{row.count} ok ({successPct}%)
                            </span>
                          </div>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-surface-alt overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gold-gradient transition-all duration-500"
                            style={{ width: `${widthPct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* ── By provider ──────────────────────────────────────────────── */}
            <Card padded>
              <SectionHeader
                title="By provider"
                subtitle="Which model is doing the heavy lifting — and what each one is costing you"
                icon={<DollarSign size={16} />}
              />
              {data.byProvider.length === 0 ? (
                <div className="py-6 text-center text-xs text-text-muted">No usage in this window.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-2xs font-semibold uppercase tracking-wider text-text-muted">
                        <th className="px-3 py-2.5">Provider</th>
                        <th className="px-3 py-2.5 text-right">Replies</th>
                        <th className="px-3 py-2.5 text-right">Tokens</th>
                        <th className="px-3 py-2.5 text-right">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byProvider.map((row) => (
                        <tr key={row.provider} className="border-b border-border/50 last:border-0">
                          <td className="px-3 py-2.5 font-semibold text-text-primary capitalize">{row.provider}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-text-secondary">{fmtNum(row.replies)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-text-secondary">{fmtNum(row.tokens)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-text-secondary">{fmtUsd(row.costUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
